import { businessDescriptionOptions, marketingBudgetOptions } from 'screens/LoginOrSignup/components/CompanyData/utils'
import { RegistrationStepProps } from 'screens/Registration/types'
import Styles from './BusinessType.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelectionHandler } from 'screens/Registration/hooks/useSelectionHandler'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { useCallback, useState, useRef, useEffect } from 'react'
import { Item } from '@react-stately/collections'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Pressable } from '@react-aria/interactions'
import { ChevronDown2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import client from 'api/client'
import { useUpdateRegistrationData } from 'hooks/queries/useUpdateRegistrationData'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFoldersAndFiles } from 'hooks/queries/useFoldersAndFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { collectResults } from 'utils/query'
import { AlmanacFile } from 'types'
import { useUpdateAlmanacFile } from 'hooks/queries/almanacFiles/useUpdateAlmanacFile'
import { useDispatch } from 'react-redux'
import { useAuth } from 'hooks/UseAuth'
import { authMerge } from 'redux/actions'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import cx from 'classnames'

export const BusinessType: React.FC<RegistrationStepProps> = ({ onNext, onBack }) => {
  const useStrategiesLayout = true
  const currentWorkspace = useCurrentWorkspace()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [customBusinessType, setCustomBusinessType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [, setIsDrawerOpen] = useState(false)
  const { trackBusinessTypeSelected, trackMarketingBudgetSelected } = useOnboardingTracking()
  const { mutateAsync: updateRegistrationData } = useUpdateRegistrationData()
  const customInputRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const auth = useAuth()

  const folders = useFoldersAndFiles(currentWorkspaceId)
  const foldersData = collectResults(folders, (data) => data.files as AlmanacFile[]) ?? []
  const defaultFolder = foldersData.find((folder) => folder && !!folder.default_project)
  const { mutateAsync: updateDefaultFolder } = useUpdateAlmanacFile({ key: defaultFolder?.id || '' })

  const { selectedValue, handleSelection: originalHandleSelection } = useSelectionHandler({
    paramName: 'businessType',
    onNext: isMobile ? () => {} : onNext,
    dataKey: 'businessType',
  })

  const { selectedValue: marketingBudgetValue, handleSelection: marketingBudgetHandleSelection } = useSelectionHandler({
    paramName: 'marketingBudget',
    onNext: isMobile ? () => {} : onNext,
    dataKey: 'marketingBudget',
  })

  const isOtherSelected = selectedValue === 'Other'

  const handleSelection = useCallback(
    (value: string) => {
      if (value === 'Other' && isMobile) {
        originalHandleSelection('Other')
        setIsDrawerOpen(true)
      } else {
        originalHandleSelection(value)
      }
    },
    [isMobile, originalHandleSelection],
  )

  useEffect(() => {
    if (!isOtherSelected || !customInputRef.current || isMobile) return

    requestAnimationFrame(() => {
      const candidates = [
        document.querySelector<HTMLElement>('main'),
        customInputRef.current?.closest<HTMLElement>('[style*="overflow"]'),
        customInputRef.current?.closest<HTMLElement>('[class*="root"]'),
        document.documentElement,
      ]

      const scrollContainer = candidates.find((el) => el && el.scrollHeight > el.clientHeight)
      if (!scrollContainer) return

      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' })
      setTimeout(() => customInputRef.current?.querySelector<HTMLInputElement>('input')?.focus(), 200)
    })
  }, [isOtherSelected, isMobile])

  const handleNextWithTracking = useCallback(async () => {
    setIsLoading(true)

    const effectiveBusinessType =
      isOtherSelected && customBusinessType.trim() ? customBusinessType.trim() : selectedValue

    if (!isMobile && effectiveBusinessType) {
      trackBusinessTypeSelected(String(effectiveBusinessType))
      trackMarketingBudgetSelected(String(marketingBudgetValue))
    }

    // Update user profile, workspace name, and default folder to use the Full Name provided by the user
    if (name) {
      const [first_name, ...rest] = name.split(' ')
      client.updateUserProfile({ first_name, last_name: rest.join(' ') })

      const last_name = rest.length > 0 ? rest.join(' ') : ''

      if (currentWorkspace) {
        let workspaceName = `${first_name}'s Workspace`
        if (last_name) {
          workspaceName = `${first_name} ${last_name}'s Workspace`
        }

        client.updateWorkspaceSettings(
          {
            workspace: {
              name: workspaceName,
            },
          },
          currentWorkspace?.key,
        )
      }

      dispatch(
        authMerge({
          auth: {
            ...auth,
            display_name: last_name ? `${first_name} ${last_name}` : first_name,
            first_name,
            last_name,
          },
        }),
      )

      if (defaultFolder) {
        updateDefaultFolder({
          title: `${first_name}'s Documents`,
        })
      }
    }

    if (effectiveBusinessType) {
      await updateRegistrationData({
        businessType: String(effectiveBusinessType),
        marketingBudget: String(marketingBudgetValue),
        isMobile,
      })
    }

    onNext?.()
    setIsLoading(false)
  }, [
    isOtherSelected,
    customBusinessType,
    selectedValue,
    isMobile,
    marketingBudgetValue,
    name,
    onNext,
    trackBusinessTypeSelected,
    trackMarketingBudgetSelected,
    currentWorkspace,
    dispatch,
    auth,
    defaultFolder,
    updateDefaultFolder,
    updateRegistrationData,
  ])

  const isNextDisabled =
    !selectedValue ||
    isLoading ||
    !name.length ||
    (isOtherSelected && !customBusinessType.trim()) ||
    (useStrategiesLayout && !marketingBudgetValue)

  if (useStrategiesLayout) {
    return (
      <div className={Styles.strategiesContainer}>
        <div className={Styles.leftSide}>
          <div className={Styles.leftContent}>
            <div className={Styles.top}>
              <div className={Styles.texts}>
                <Heading level={1}>Your content. Every channel. Starting today.</Heading>
                <Text color="var(--dark-60)">Let's personalize your experience.</Text>
              </div>

              <div className={Styles.fieldGroup}>
                <label htmlFor="name">
                  <Text variant="label" color="var(--dark-60)">
                    Your full name
                  </Text>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => {
                    setName((e.target as HTMLInputElement).value)
                    setNameError('')
                  }}
                  size="xl"
                  placeholder="Enter your name..."
                  autoFocus
                />
                {nameError && (
                  <div className={Styles.error}>
                    <FormErrorMessage className={Styles.errorMessage}>{nameError}</FormErrorMessage>
                  </div>
                )}
              </div>
              <div className={Styles.fieldGroup}>
                <Text variant="label" color="var(--dark-60)">
                  What’s your monthly marketing budget
                </Text>
                <MenuTrigger placement="bottom" popoverClassName={Styles.popover}>
                  <Pressable>
                    <div className={cx(Styles.selectCategory, Styles.forStrategies)} tabIndex={0} role="button">
                      {marketingBudgetValue ? (
                        <div className={Styles.menuItem}>
                          <Text>
                            {marketingBudgetOptions.find((option) => option.value === marketingBudgetValue)?.title}
                          </Text>
                        </div>
                      ) : (
                        <Text color="var(--dark-40)">Select a budget range</Text>
                      )}
                      <ChevronDown2 size={16} />
                    </div>
                  </Pressable>
                  <Menu
                    onAction={(key) => marketingBudgetHandleSelection(String(key))}
                    aria-label="Business type"
                    selectionMode="single"
                    selectedKeys={[marketingBudgetValue]}
                  >
                    {marketingBudgetOptions.map((option) => (
                      <Item key={option.value} textValue={option.value}>
                        <div className={Styles.menuItem}>
                          <Text variant="secondary">{option.title}</Text>
                        </div>
                      </Item>
                    ))}
                  </Menu>
                </MenuTrigger>
              </div>
              <div className={Styles.fieldGroup}>
                <Text variant="label" color="var(--dark-60)">
                  Your business category
                </Text>
                <MenuTrigger placement="bottom" popoverClassName={Styles.popover}>
                  <Pressable>
                    <div className={cx(Styles.selectCategory, Styles.forStrategies)} tabIndex={0} role="button">
                      {selectedValue ? (
                        <div className={Styles.menuItem}>
                          {businessDescriptionOptions.find((option) => option.title === selectedValue)?.icon}
                          <Text>{selectedValue}</Text>
                        </div>
                      ) : (
                        <Text color="var(--dark-40)">Select a category</Text>
                      )}
                      <ChevronDown2 size={16} />
                    </div>
                  </Pressable>
                  <Menu
                    onAction={(key) => handleSelection(String(key))}
                    aria-label="Business type"
                    selectionMode="single"
                    selectedKeys={[selectedValue]}
                  >
                    {businessDescriptionOptions.map((option) => (
                      <Item key={option.title} textValue={option.title}>
                        <div className={Styles.menuItem}>
                          {option.icon}
                          <Text variant="secondary">{option.title}</Text>
                        </div>
                      </Item>
                    ))}
                  </Menu>
                </MenuTrigger>
              </div>
              {isOtherSelected && (
                <div className={Styles.fieldGroup} ref={customInputRef}>
                  <Text variant="label" color="var(--dark-60)">
                    Describe your business category
                  </Text>
                  <Input
                    id="custom-business-type"
                    name="customBusinessType"
                    value={customBusinessType}
                    onChange={(e) => setCustomBusinessType((e.target as HTMLInputElement).value)}
                    size="lg"
                    placeholder="Enter your business category"
                    autoFocus
                  />
                </div>
              )}
            </div>
            <Button
              size={isMobile ? 'lg' : 'xl'}
              onPress={handleNextWithTracking}
              isDisabled={isNextDisabled}
              fullWidth={isMobile}
            >
              Continue
            </Button>
          </div>
        </div>
        <img
          className={Styles.blazeIllustration}
          alt="blaze illustration"
          src="https://res.cloudinary.com/almanac/image/upload/v1777049193/blaze_assets/virtual_marketer/onboarding/content_plan_example_rgqjl1.png"
        />
      </div>
    )
  }

  return (
    <MultiStepStepLayout
      title="Tell us a little bit about yourself"
      isNextButtonDisabled={isNextDisabled}
      onNext={handleNextWithTracking}
      scrollContainerClassName={Styles.container}
      centeredFooter
      withArrowRight={false}
      mobileNextButtonSize="lg"
    >
      <div className={Styles.fieldsContainer}>
        <div className={Styles.fieldGroup}>
          <label htmlFor="name">
            <Text variant="label" color="var(--dark-60)">
              Your full name
            </Text>
          </label>
          <Input
            id="name"
            name="name"
            value={name}
            onChange={(e) => {
              setName((e.target as HTMLInputElement).value)
              setNameError('')
            }}
            size={isMobile ? 'lg' : 'xl'}
            placeholder="Enter your name..."
            autoFocus
          />
          {nameError && (
            <div className={Styles.error}>
              <FormErrorMessage className={Styles.errorMessage}>{nameError}</FormErrorMessage>
            </div>
          )}
        </div>
        <div className={Styles.fieldGroup}>
          <Text variant="label" color="var(--dark-60)">
            Your business category
          </Text>
          <MenuTrigger placement="bottom" popoverClassName={Styles.businessTypePopover}>
            <Pressable>
              <div className={Styles.selectCategory} tabIndex={0} role="button">
                {selectedValue ? (
                  <div className={Styles.menuItem}>
                    {businessDescriptionOptions.find((option) => option.title === selectedValue)?.icon}
                    <Text>{selectedValue}</Text>
                  </div>
                ) : (
                  <Text color="var(--dark-40)">Select a category</Text>
                )}
                <ChevronDown2 size={16} />
              </div>
            </Pressable>
            <Menu
              onAction={(key) => handleSelection(String(key))}
              aria-label="Business type"
              selectionMode="single"
              selectedKeys={[selectedValue]}
            >
              {businessDescriptionOptions.map((option) => (
                <Item key={option.title} textValue={option.title}>
                  <div className={Styles.menuItem}>
                    {option.icon}
                    <Text variant="secondary">{option.title}</Text>
                  </div>
                </Item>
              ))}
            </Menu>
          </MenuTrigger>
        </div>
        {isOtherSelected && (
          <div className={Styles.fieldGroup} ref={customInputRef}>
            <Text variant="label" color="var(--dark-60)">
              Describe your business category
            </Text>
            <Input
              id="custom-business-type"
              name="customBusinessType"
              value={customBusinessType}
              onChange={(e) => setCustomBusinessType((e.target as HTMLInputElement).value)}
              size="xl"
              placeholder="Enter your business category"
              autoFocus
            />
          </div>
        )}
      </div>
    </MultiStepStepLayout>
  )
}
